<script setup lang="ts">
import { Check, Copy } from '@lucide/vue'
import { Button } from '@zerohuman/ui'
import { onUnmounted, ref } from 'vue'

const props = defineProps<{ value: string; label?: string }>()

const copied = ref(false)
let clear: ReturnType<typeof setTimeout> | null = null

/**
 * The clipboard API needs a secure context and the user's permission, and there is no useful recovery when it
 * refuses: the text is on screen to select by hand. So a refusal simply leaves the button unticked.
 */
async function copy(): Promise<void> {
    try {
        await navigator.clipboard?.writeText(props.value)
        copied.value = true
        if (clear) {
            clearTimeout(clear)
        }
        clear = setTimeout(() => {
            copied.value = false
        }, 1500)
    } catch {
        copied.value = false
    }
}

onUnmounted(() => {
    if (clear) {
        clearTimeout(clear)
    }
})
</script>

<template>
	<Button
		type="button"
		size="sm"
		variant="outline"
		data-testid="copy-button"
		:aria-label="copied ? 'Copied' : `Copy ${props.label ?? 'to clipboard'}`"
		@click="copy"
	>
		<component :is="copied ? Check : Copy" class="size-3.5" aria-hidden="true" />
		{{ copied ? 'Copied' : 'Copy' }}
	</Button>
</template>
